import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  styled,
  alpha,
  useTheme,
  useMediaQuery,
  Tooltip,
  TextField,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogContent,
  Grid,
  Avatar,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import PaymentsIcon from "@mui/icons-material/Payments";
import PersonIcon from "@mui/icons-material/Person";
import InfoIcon from "@mui/icons-material/Info";

// Import the component we created
import AddNewForcedPayment from "./AddNewForcedPayment";

const PRIMARY_COLOR = "#0a2e52";
const SECONDARY_COLOR = "#d32f2f";

const ModernTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(1.8, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: alpha(theme.palette.primary.main, 0.015),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.045),
    transition: "background-color 0.2s ease",
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  "&::-webkit-scrollbar": {
    height: "8px",
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.4),
    },
  },
}));

// Utility component for displaying view modal data beautifully
const InfoBox = ({ label, value }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: "8px",
      backgroundColor: "#fff",
      border: "1px solid #e2e8f0",
      height: "100%",
    }}
  >
    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color: PRIMARY_COLOR, mt: 0.5, wordBreak: "break-word" }}>
      {value || "-"}
    </Typography>
  </Box>
);

function ForcedPayments() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [forcedPayments, setForcedPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "employeeId",
    direction: "asc",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedForcedPayment, setSelectedForcedPayment] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 5;

  useEffect(() => {
    fetchForcedPayments();
  }, []);

  const fetchForcedPayments = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/support/forced-payments");
      if (!res.ok) {
        throw new Error(
          `Failed to fetch forced payments. Status: ${res.status}`,
        );
      }
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.recordset || [];
      setForcedPayments(list);
    } catch (error) {
      console.error("Error fetching forced payments:", error);
      setForcedPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }
    setSortConfig({ key, direction });
  };

  const filteredForcedPayments = forcedPayments.filter((item) => {
    const search = searchTerm.toLowerCase().trim();
    if (!search) return true;
    return (
      item.employeeId?.toString().toLowerCase().includes(search) ||
      item.firstName?.toLowerCase().includes(search) ||
      item.lastName?.toLowerCase().includes(search)
    );
  });

  const sortedForcedPayments = [...filteredForcedPayments].sort((a, b) => {
    let aValue =
      sortConfig.key === "name"
        ? `${a.firstName} ${a.lastName}`
        : a[sortConfig.key];
    let bValue =
      sortConfig.key === "name"
        ? `${b.firstName} ${b.lastName}`
        : b[sortConfig.key];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
    }

    const comparison = String(aValue)
      .toLowerCase()
      .localeCompare(String(bValue).toLowerCase());

    return sortConfig.direction === "asc" ? comparison : -comparison;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedForcedPayments.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedForcedPayment(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddNew = () => {
    setAddModalOpen(true);
  };

  const handleView = () => {
    if (!selectedForcedPayment) return;
    setViewModalOpen(true);
    handleMenuClose();
  };

  const handleDelete = () => {
    if (!selectedForcedPayment) return;
    setDeleteModalOpen(true);
    handleMenuClose();
  };

  const formatDate = (date) => {
    if (!date) return "-";
    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) return date;
    return parsedDate.toLocaleString("en-RW", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const calculateTotal = (payment) => {
    if (!payment) return 0;
    return (
      (Number(payment.basePay) || 0) +
      (Number(payment.housingAllowance) || 0) +
      (Number(payment.transportAllowance) || 0) +
      (Number(payment.otherAllowance) || 0)
    );
  };

  const formatCurrency = (amount) => {
    return Number(amount || 0).toLocaleString();
  };

  const columns = [
    { id: "employeeId", label: "Employee ID", width: "12%" },
    { id: "name", label: "Employee Name", width: "18%" },
    { id: "basePay", label: "Base Pay", width: "15%" },
    { id: "housingAllowance", label: "Housing", width: "12%" },
    { id: "transportAllowance", label: "Transport", width: "12%" },
    { id: "otherAllowance", label: "Other", width: "12%" },
    { id: "actions", label: "Actions", width: "10%" },
  ];

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: { xs: 2, sm: 3 },
        mb: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          mb: 3,
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: PRIMARY_COLOR }}
          >
            Forced Payments
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search by ID or Name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            sx={{ width: { xs: "100%", sm: 280 } }}
          />

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{ minHeight: 40, whiteSpace: "nowrap", px: 2, borderRadius: "8px" }}
          >
            Add New
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing <strong>{filteredForcedPayments.length}</strong>{" "}
          {filteredForcedPayments.length === 1
            ? "forced payment"
            : "forced payments"}
        </Typography>
      </Box>

      <ResponsiveTableContainer component={Paper} elevation={0}>
        <Table size={isMobile ? "small" : "medium"} sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: theme.palette.primary.light,
                "& th": {
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  whiteSpace: "nowrap",
                  borderBottom: "none",
                  py: 1.8,
                },
              }}
            >
              {columns.map((column) => (
                <ModernTableCell
                  key={column.id}
                  onClick={() => {
                    if (column.id !== "actions") {
                      handleSort(column.id);
                    }
                  }}
                  sx={{
                    width: column.width,
                    cursor: column.id !== "actions" ? "pointer" : "default",
                    "&:hover":
                      column.id !== "actions"
                        ? { backgroundColor: alpha("#fff", 0.08) }
                        : {},
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.8}>
                    {column.label}
                    {sortConfig.key === column.id && column.id !== "actions" && (
                      <Box
                        display="flex"
                        flexDirection="column"
                        sx={{ lineHeight: 0.5 }}
                      >
                        <span
                          style={{
                            fontSize: "0.6rem",
                            opacity: sortConfig.direction === "asc" ? 1 : 0.4,
                          }}
                        >
                          ▲
                        </span>
                        <span
                          style={{
                            fontSize: "0.6rem",
                            opacity: sortConfig.direction === "desc" ? 1 : 0.4,
                          }}
                        >
                          ▼
                        </span>
                      </Box>
                    )}
                  </Box>
                </ModernTableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <ModernTableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1.5 }}
                  >
                    Loading forced payments...
                  </Typography>
                </ModernTableCell>
              </TableRow>
            ) : currentItems.length > 0 ? (
              currentItems.map((item) => (
                <StyledTableRow key={item.employeeId} hover>
                  <ModernTableCell>
                    <Chip
                      label={item.employeeId}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        color: PRIMARY_COLOR,
                        backgroundColor: alpha(PRIMARY_COLOR, 0.08),
                        borderRadius: "6px",
                      }}
                    />
                  </ModernTableCell>

                  <ModernTableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: PRIMARY_COLOR }}
                    >
                      {item.firstName} {item.lastName}
                    </Typography>
                  </ModernTableCell>

                  <ModernTableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(item.basePay)}
                    </Typography>
                  </ModernTableCell>

                  <ModernTableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(item.housingAllowance)}
                    </Typography>
                  </ModernTableCell>

                  <ModernTableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(item.transportAllowance)}
                    </Typography>
                  </ModernTableCell>

                  <ModernTableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(item.otherAllowance)}
                    </Typography>
                  </ModernTableCell>

                  <ModernTableCell>
                    <Tooltip title="More actions" arrow>
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, item)}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          color: PRIMARY_COLOR,
                          borderRadius: "8px",
                          "&:hover": {
                            backgroundColor: alpha(PRIMARY_COLOR, 0.08),
                          },
                        }}
                      >
                        <MoreVertIcon fontSize={isMobile ? "small" : "medium"} />
                      </IconButton>
                    </Tooltip>
                  </ModernTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <ModernTableCell colSpan={7} align="center" sx={{ py: 7 }}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "text.secondary" }}
                  >
                    No forced payments found
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Try changing your search criteria.
                  </Typography>
                </ModernTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>
      
      {filteredForcedPayments.length > itemsPerPage && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={Math.ceil(filteredForcedPayments.length / itemsPerPage)}
            page={currentPage}
            onChange={(e, value) => setCurrentPage(value)}
            color="primary"
            size={isMobile ? "small" : "medium"}
            sx={{
              "& .MuiPaginationItem-root": {
                "&.Mui-selected": {
                  bgcolor: PRIMARY_COLOR,
                  "&:hover": { bgcolor: alpha(PRIMARY_COLOR, 0.8) },
                },
              },
            }}
          />
        </Box>
      )}

      {/* ACTION MENU */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 160,
            borderRadius: "10px",
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <MenuItem
          onClick={handleView}
          sx={{
            gap: 1.5,
            py: 1.3,
            px: 2,
            "&:hover": { backgroundColor: alpha(PRIMARY_COLOR, 0.05) },
          }}
        >
          <VisibilityIcon fontSize="small" sx={{ color: PRIMARY_COLOR }} />
          <Typography variant="body2">View Details</Typography>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={handleDelete}
          sx={{
            gap: 1.5,
            py: 1.3,
            px: 2,
            "&:hover": { backgroundColor: alpha(SECONDARY_COLOR, 0.06) },
          }}
        >
          <DeleteIcon fontSize="small" sx={{ color: SECONDARY_COLOR }} />
          <Typography variant="body2" sx={{ color: SECONDARY_COLOR }}>
            Delete Record
          </Typography>
        </MenuItem>
      </Menu>

      {/* ======================================================
          VIEW MODAL - HARMONIZED & REDESIGNED
      ====================================================== */}
      <Dialog
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedForcedPayment(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            overflow: "hidden",
            backgroundColor: "#f8fafc", // Light, modern background
          },
        }}
      >
        {selectedForcedPayment && (
          <>
            {/* Header matches AddNewForcedPayment */}
            <Box
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                px: 3,
                py: 2.5,
                position: "relative",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.15)",
                    width: 56,
                    height: 56,
                    border: "2px solid rgba(255,255,255,0.3)",
                  }}
                >
                  <PersonIcon fontSize="large" />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ color: "white", fontWeight: 700, lineHeight: 1.2 }}>
                    {selectedForcedPayment.firstName} {selectedForcedPayment.lastName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", mt: 0.5 }}>
                    Employee ID: <strong>{selectedForcedPayment.employeeId}</strong>
                  </Typography>
                  <Chip
                    label="Forced Payment Record"
                    size="small"
                    sx={{
                      mt: 1,
                      color: "white",
                      bgcolor: "rgba(255,255,255,0.2)",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                    }}
                  />
                </Box>
              </Box>

              <IconButton
                onClick={() => {
                  setViewModalOpen(false);
                  setSelectedForcedPayment(null);
                }}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <DialogContent sx={{ p: 3 }}>
              {/* Employee & Payroll Mapping Section */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <InfoIcon sx={{ color: PRIMARY_COLOR, fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ color: PRIMARY_COLOR, fontWeight: 700 }}>
                    Payroll Configuration
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoBox label="Employee Group" value={selectedForcedPayment.employeeGroup} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoBox label="Payroll Group" value={selectedForcedPayment.payrollGroup} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoBox label="Source of Fund" value={selectedForcedPayment.sourceOfFund} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoBox label="Cost Center" value={selectedForcedPayment.Costcenter} />
                  </Grid>
                </Grid>
              </Box>

              {/* Payment Breakdown Section */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <PaymentsIcon sx={{ color: PRIMARY_COLOR, fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ color: PRIMARY_COLOR, fontWeight: 700 }}>
                    Compensation Breakdown
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <InfoBox label="Base Pay" value={formatCurrency(selectedForcedPayment.basePay)} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <InfoBox label="Housing" value={formatCurrency(selectedForcedPayment.housingAllowance)} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <InfoBox label="Transport" value={formatCurrency(selectedForcedPayment.transportAllowance)} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <InfoBox label="Other" value={formatCurrency(selectedForcedPayment.otherAllowance)} />
                  </Grid>
                </Grid>

                {/* Total Row */}
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: "8px",
                    backgroundColor: alpha(PRIMARY_COLOR, 0.05),
                    border: `1px solid ${alpha(PRIMARY_COLOR, 0.1)}`,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <Typography variant="subtitle1" sx={{ color: PRIMARY_COLOR, fontWeight: 600 }}>
                    Total Gross Payment Amount
                  </Typography>
                  <Typography variant="h6" sx={{ color: PRIMARY_COLOR, fontWeight: 800 }}>
                    {formatCurrency(calculateTotal(selectedForcedPayment))}
                  </Typography>
                </Box>
              </Box>

              {/* Meta Data Section */}
              <Box>
                <Typography variant="subtitle1" sx={{ color: PRIMARY_COLOR, fontWeight: 700, mb: 1.5 }}>
                  Additional Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <InfoBox label="Created By" value={selectedForcedPayment.CreatedBy} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InfoBox label="Created On" value={formatDate(selectedForcedPayment.createdOn)} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InfoBox label="Reason" value={selectedForcedPayment.reason} />
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* ======================================================
          ADD MODAL
      ====================================================== */}
      <AddNewForcedPayment
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={() => {
          setAddModalOpen(false);
          fetchForcedPayments();
        }}
      />

      {/* ======================================================
          DELETE MODAL (Placeholder)
      ====================================================== */}
      {/*
      <DeleteForcedPayment
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        paymentData={selectedForcedPayment}
        onDeleteSuccess={fetchForcedPayments}
      />
      */}
    </Box>
  );
}

export default ForcedPayments;